import React, { useState, useRef, useEffect } from 'react';
import { Input, HStack, Text } from '@chakra-ui/react';

interface OTPInputProps {
  onOTPChange: (otp: string) => void;
  isDisabled?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({ onOTPChange, isDisabled = false }) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0]?.focus();
    }
  }, []);

  useEffect(() => {
    const otpString = otp.join('');
    onOTPChange(otpString);
  }, [otp, onOTPChange]);

    const handleChange = (index: number, value: string) => {
        const numericValue = value.replace(/\D/g, ''); // keep only digits

        if (numericValue === '' || /^[0-9]$/.test(numericValue)) {
            const newOtp = [...otp];
            newOtp[index] = numericValue;
            setOtp(newOtp);

            if (numericValue && index < 5) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const newOtp = [...otp];
      
      if (otp[index]) {
        newOtp[index] = '';
        setOtp(newOtp);
      } else if (index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault();
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const numericData = pastedData.replace(/\D/g, '').slice(0, 6);
    
    if (numericData) {
      const newOtp = [...otp];
      for (let i = 0; i < 6; i++) {
        newOtp[i] = numericData[i] || '';
      }
      setOtp(newOtp);
      
      const nextEmptyIndex = newOtp.findIndex(val => val === '');
      const focusIndex = nextEmptyIndex === -1 ? 5 : Math.min(nextEmptyIndex, 5);
      inputRefs.current[focusIndex]?.focus();
    }
  };

const handleFocus = (index: number) => {
    inputRefs.current[index]?.select();
  };

  return (
  <HStack spacing={2} justify="center">
    {otp.map((digit, index) => (
      <React.Fragment key={index}>
        <Input
          ref={(el) => (inputRefs.current[index] = el)}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          maxLength={1}
          textAlign="center"
          size="lg"
          width="50px"
          height="50px"
          fontSize="xl"
          fontWeight="bold"
          isDisabled={isDisabled}
          variant="filled"
          _focus={{
            borderColor: "blue.500",
            boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
          }}
        />

        {/* Add dash after the 3rd input */}
        {index === 2 && (
          <Text fontSize="20px" paddingX={1} color={"gray"} fontWeight="semibold">
            -
          </Text>
        )}
      </React.Fragment>
    ))}
  </HStack>
  );
};

export default OTPInput